// App state. Everything else is fetched per screen — this app has one user and
// small payloads, so a cache layer would cost more than it saves.
//
// There is no sign-in, so the only states are: still loading, needs setting up,
// and ready.

import { create } from 'zustand';
import { api } from './api.js';

export const useSession = create((set, get) => ({
  status: 'loading', // loading | onboarding | ready | offline
  user: null,
  profile: null,
  health: null,
  settings: null,
  latestEvent: null,
  error: null,

  async bootstrap() {
    try {
      const data = await api.get('/me');
      applyTheme(data.settings?.theme || 'light');
      set({
        status: data.onboarded ? 'ready' : 'onboarding',
        user: data.user,
        profile: data.profile,
        health: data.health,
        settings: data.settings,
        latestEvent: data.latestEvent,
        error: null,
      });
    } catch (err) {
      set({ status: 'offline', error: err.message });
    }
  },

  setProfile(profile) {
    set({ profile });
  },

  setName(name) {
    set({ user: { ...(get().user || {}), name } });
    api.patch('/me', { name }).catch(() => {});
  },

  setTheme(theme) {
    applyTheme(theme);
    set({ settings: { ...(get().settings || {}), theme } });
    api.patch('/profile', { theme }).catch(() => {});
  },
}));

export function applyTheme(theme) {
  const t = theme === 'dark' ? 'dark' : 'light';
  document.documentElement.dataset.theme = t;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', t === 'dark' ? '#17120f' : '#faf7f5');
}

// Small transient banner for confirmations and errors.
export const useToast = create((set) => ({
  toast: null,
  show(message, tone = 'good') {
    set({ toast: { message, tone, id: Date.now() } });
    setTimeout(() => set((s) => (s.toast && Date.now() - s.toast.id >= 3400 ? { toast: null } : s)), 3500);
  },
  clear: () => set({ toast: null }),
}));
